import { useRoute, useRouter } from 'vue-router'

import { AssetClass, SearchSummary, TagClass, TransactionClass } from '@types'

import { ROUTE_NAME } from '@/const'

import { useNotifications } from '@/plugins/notification'

import { useModalsStore } from '@/store/modals'
import { useTransactionsBunchStore } from '@/store/transactions/bunch'
import { useTransactionsStore } from '@/store/transactions'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTagsBunchStore } from '@/store/tags/bunch'

export const useGlobalSearch = () => {
  const route = useRoute()
  const router = useRouter()

  const { error } = useNotifications()

  const assetsBunchStore = useAssetsBunchStore()
  const modalsStore = useModalsStore()
  const tagsBunchStore = useTagsBunchStore()
  const transactionsBunchStore = useTransactionsBunchStore()
  const transactionsStore = useTransactionsStore()

  const handleSearchResult = async (item: SearchSummary) => {
    if (!item.route.params?.id) {
      error({ message: 'This item has no id' })
      return
    }
    let instance: AssetClass | TagClass | TransactionClass | undefined
    switch (item.route.name) {
      case ROUTE_NAME.ASSETS_ITEM: {
        let asset = assetsBunchStore.getElementById(item.route.params.id as string)
        if (!asset) {
          error({ message: 'Asset not found' })
          return
        }
        if ([
          ROUTE_NAME.ASSETS,
          ROUTE_NAME.ASSETS_ITEM,
          ROUTE_NAME.ASSETS_ITEM_SECURITY_MASTER,
          ROUTE_NAME.ASSETS_ITEM_PRICES,
          ROUTE_NAME.ASSETS_ITEM_PRICES_TABLE,
          ROUTE_NAME.ASSETS_ITEM_TAGS,
          ROUTE_NAME.ASSETS_ITEM_CONTACTS,
          ROUTE_NAME.ASSETS_ITEM_DOCUMENTS,
          ROUTE_NAME.ASSETS_ITEM_POSITIONS,
          ROUTE_NAME.ASSETS_ITEM_TRANSACTIONS,
          ROUTE_NAME.ASSETS_ITEM_VALUES,
          ROUTE_NAME.CURRENCIES_ITEM,
          ROUTE_NAME.CURRENCIES_ITEM_PRICES,
          ROUTE_NAME.CURRENCIES_ITEM_PRICES_TABLE,
          ROUTE_NAME.CURRENCIES_ITEM_TAGS,
          ROUTE_NAME.CURRENCIES_ITEM_SECURITY_MASTER,
          ROUTE_NAME.INCOME_ACCOUNTS_ITEM,
          ROUTE_NAME.EXPENSE_ACCOUNTS_ITEM,
        ].includes(route.name as string)) {
          router.replace(item.route)
          return
        }
        instance = asset
        break
      }
      case ROUTE_NAME.TAGS_ITEM: {
        let tag = tagsBunchStore.getElementById(item.route.params.id as string)
        if (!tag) {
          error({ message: 'Tag not found' })
          return
        }
        if ([ROUTE_NAME.TAGS, ROUTE_NAME.TAGS_ITEM].includes(route.name as string)) {
          router.replace(item.route)
          return
        }
        instance = tag
        break
      }
      case ROUTE_NAME.INCOME_ACCOUNTS_ITEM: {
        let incomeAccount = assetsBunchStore.getElementById(item.route.params.id as string)
        if (!incomeAccount) {
          error({ message: 'Income account not found' })
          return
        }
        if ([ROUTE_NAME.INCOME_ACCOUNTS, ROUTE_NAME.INCOME_ACCOUNTS_ITEM].includes(route.name as string)) {
          router.replace(item.route)
          return
        }
        instance = incomeAccount
        break
      }
      case ROUTE_NAME.EXPENSE_ACCOUNTS_ITEM: {
        let expenseAccount = assetsBunchStore.getElementById(item.route.params.id as string)
        if (!expenseAccount) {
          error({ message: 'Income account not found' })
          return
        }
        if ([ROUTE_NAME.EXPENSE_ACCOUNTS, ROUTE_NAME.EXPENSE_ACCOUNTS_ITEM].includes(route.name as string)) {
          router.replace(item.route)
          return
        }
        instance = expenseAccount
        break
      }
      case ROUTE_NAME.TRANSACTIONS_ITEM: {
        let transaction = transactionsBunchStore.getElementById(item.route.params.id as string)
        if ([ROUTE_NAME.TRANSACTIONS, ROUTE_NAME.TRANSACTIONS_ITEM].includes(route.name as string) && transaction) {
          router.replace(item.route)
          return
        } else if (!transaction) {
          const result = await transactionsStore.fetchSingle(item.route.params.id as string)
          transaction = new TransactionClass(result)
        }
        if (!transaction) {
          error({ message: 'Transaction not found' })
          return
        }
        instance = transaction
        break
      }
    }
    if (!instance?.id) {
      error({ message: 'Unknown item' })
      return
    }
    const modalInstance = modalsStore.init(instance.id, instance, { allowTray: true })
    modalInstance?.open(modalsStore.getZIndex())
  }

  return {
    handleSearchResult
  }
}
